/* ═══════════════════════════════════════════════════════════════
   pageGates — the ONE predicate per sidebar page that decides whether a
   viewer may open it. Extracted from app.tsx's sidebar nav filter
   (Project Canvas R1, T3b) so the sidebar filter AND the project
   dashboard's nested-tool host (Dashboard.tsx / ProjectToolHost) read the
   exact same keys and can never drift (Decision 13 — no new permission
   keys, same gate everywhere a page can be reached).

   Deliberately plain `Array.includes` checks, NOT `hasPermission` — the
   sidebar filter never expanded the `'*'` wildcard before this extraction,
   so reusing `hasPermission` here would silently grant admins ('*') nav
   items they could not see today. Behavior-identical, not "improved".
   ═══════════════════════════════════════════════════════════════ */

/** The sidebar pages a project-dashboard widget can nest/drill into
 *  (Project Canvas R1, Decision 5/6). 'team' is included only because the
 *  People page gate is read the same way — the Team drill itself stays a
 *  real `navigate` (Decision 14 exception), never nested. */
export type GatedTool = 'tasks' | 'invoices' | 'forecasting' | 'time-expense' | 'team';

export function pageGate(tool: GatedTool, perms: string[] | undefined | null): boolean {
  const up = perms || [];
  switch (tool) {
    case 'tasks':
      // Tasks Kit Decision 16: any of the three view keys. Deliberately
      // NOT imported from taskPermissions.ts's canSeeTasks (which uses
      // hasAnyPermission, expanding '*') to keep this module's plain
      // Array.includes contract — same `*`-non-expanding behavior the
      // other cases here already rely on.
      return (
        up.includes('tasks.view_own') ||
        up.includes('tasks.view_team') ||
        up.includes('tasks.view_all')
      );
    case 'invoices':
      return (
        up.includes('invoices.view_own') ||
        up.includes('invoices.view_all') ||
        up.includes('invoices.view') // legacy fallback
      );
    case 'forecasting':
      return (
        up.includes('forecasting.view_own') ||
        up.includes('forecasting.view_team') ||
        up.includes('forecasting.view_all') ||
        up.includes('forecasting.edit_own') ||
        up.includes('forecasting.edit_team') ||
        up.includes('forecasting.edit_any')
      );
    case 'time-expense':
      return up.includes('timesheets.view_own') || up.includes('timesheets.create');
    case 'team':
      return up.includes('team_members.view');
    default:
      return false;
  }
}
